import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { DeleteAccountDialogService } from '@libs/src/delete-account-dialog/delete-account-dialog.service';
import { ToastService } from '@libs/src/toast/toast.service';
import { ProfileIcebreakersEditDialogService } from '@main-client/src/app/profile/profile-icebreakers-edit-dialog/profile-icebreakers-edit-dialog.service';
import { ProfileIntroEditDialogService } from '@main-client/src/app/profile/profile-intro-edit-dialog/profile-intro-edit-dialog.service';
import { UploadService } from '@main-client/src/app/upload/upload.service';

import { ProfileFormDialogHeadlineComponent } from '@main-client/src/app/profile-form-dialogs/profile-form-headline/profile-form-dialog-headline.component';
import { DialogImageUploadComponent } from '@main-client/src/app/upload/dialog-image-upload/dialog-image-upload.component';

import { Account } from '@libs/src/models/account.model';
import { SetUser } from '@libs/src/user/user.actions';
import { Icebreaker } from '@main-client/src/app/icebreakers/icebreaker.model';
import { Profile } from '@main-client/src/app/profile/profile';
import {
  DeleteProfile,
  LoadProfile,
} from '@main-client/src/app/profile/profile-store/profile.actions';
import { checkCanStartConversation } from '@main-client/src/app/profile/profile.helper';
import {
  UPLOAD_IMAGE_DOCUMENT_TYPES,
  UPLOAD_IMAGE_URL,
} from '@main-client/src/app/upload/upload.helper';

import { IAppState } from '../../app.state';

import { ProfileReportDialogComponent } from '../profile-report-doalog/profile-report-dialog.component';
import { ProfileService } from '../profile.service';

import get from 'lodash-es/get';

@Component({
  selector: 'app-profile-intro',
  templateUrl: './profile-intro.component.html',
})
export class ProfileIntroComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isUser: boolean;
  @Input() profile: Profile;
  @Input() tenantIceBreakers: Icebreaker[];
  @Input() user: Account;
  @Output() profileUpdated = new EventEmitter();
  tenantState = this.store.select('tenant');
  canEdit = false;
  canStartConversation = false;
  isLoadingImage = false;
  error = '';
  dialogRef: MatDialogRef<any>;

  constructor(
    private deleteAccountDialogService: DeleteAccountDialogService,
    public dialog: MatDialog,
    public editDialogService: ProfileIntroEditDialogService,
    public profileIcebreakersEditDialogService: ProfileIcebreakersEditDialogService,
    private profileService: ProfileService,
    public store: Store<IAppState>,
    private toastService: ToastService,
    public uploadService: UploadService,
    public route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.canStartConversation = checkCanStartConversation(
      this.user,
      this.profile,
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    this.canEdit = this.isUser || this.user.isAdmin;
  }

  ngOnDestroy() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  openReportDialog() {
    return this.dialog
      .open(ProfileReportDialogComponent)
      .afterClosed()
      .pipe(
        filter((dialogAction: boolean) => dialogAction),
        switchMap(() => this.profileService.reportProfile(this.profile)),
        tap(() =>
          this.toastService.showSuccess(
            $localize`:@@customToast.reportProfile.successMessage:You've reported ${this.profile.full_name}. Our team will reach out to you via email.`,
          ),
        ),
      )
      .subscribe();
  }

  deleteAccount(account: Account) {
    this.deleteAccountDialogService
      .openDialog(account)
      .afterClosed()
      .pipe(
        filter((dialogResponse: any) => !!dialogResponse),
        tap((params: any) =>
          this.store.dispatch(DeleteProfile({ account, params })),
        ),
      )
      .subscribe();
  }

  openEditDialog(profile: Profile, user: Account) {
    this.editDialogService
      .openEditDialog(this.isUser, profile, user)
      .subscribe();
  }

  openIcebreakerEditDialog(profile: Profile, user: Account) {
    return this.profileIcebreakersEditDialogService
      .openEditDialog(this.isUser, this.tenantIceBreakers, profile, user)
      .subscribe();
  }

  openImageUploadDialog(account: Account) {
    const url = this.user.isAdmin
      ? UPLOAD_IMAGE_URL.admin(this.profile._id)
      : UPLOAD_IMAGE_URL.account;
    const documentType = this.user.isAdmin
      ? `${UPLOAD_IMAGE_DOCUMENT_TYPES.admin}`
      : `${UPLOAD_IMAGE_DOCUMENT_TYPES.account}`;
    const config = {
      autoFocus: false,
      data: {
        documentType,
        customUploadUrl: url,
        imageUrl: account.portrait_original_url,
      },
      maxWidth: '500px',
      width: '95vw',
    };
    this.dialog
      .open(DialogImageUploadComponent, config)
      .afterClosed()
      .pipe(
        filter((data: any) => !!data),
        map((data: any) => {
          const updatedProfile = {
            ...account,
            portrait_original_url: data.imageUrl,
          };
          this.store.dispatch(LoadProfile(updatedProfile));
          if (this.isUser) {
            this.store.dispatch(SetUser(updatedProfile));
          }
        }),
      )
      .subscribe();
  }

  deleteImage() {
    const url = this.user.isAdmin
      ? UPLOAD_IMAGE_URL.admin(this.profile._id)
      : UPLOAD_IMAGE_URL.account;
    const documentType = this.user.isAdmin
      ? UPLOAD_IMAGE_DOCUMENT_TYPES.admin
      : UPLOAD_IMAGE_DOCUMENT_TYPES.account;
    this.isLoadingImage = true;
    this.uploadService
      .removeImage(documentType, url)
      .pipe(
        tap(() => {
          this.isLoadingImage = false;
          const updatedProfile = {
            ...this.profile,
            portrait_original_url: '',
          };
          this.store.dispatch(LoadProfile(updatedProfile));
          if (this.isUser) {
            this.store.dispatch(SetUser(updatedProfile));
          }
          this.error = '';
          this.toastService.showSuccess('Update successful!');
        }),
        catchError((response: any) => {
          this.isLoadingImage = false;
          this.error = get(response, 'error.message', response.message);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  openHeadlineDialog(profile: Account, user: Account) {
    this.dialog.open(ProfileFormDialogHeadlineComponent, {
      data: { user },
      maxWidth: '500px',
      width: '95vw',
    });
  }
}
